<script setup>
import { computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { flowThreadContextKey } from './flowThreadContext';
import NodeBadge from '../ui/NodeBadge.vue';
import NodeInlineField from '../ui/NodeInlineField.vue';
import NodePanelInput from '../ui/NodePanelInput.vue';
import NodeSegmentedControl from '../ui/NodeSegmentedControl.vue';

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const context = inject(flowThreadContextKey);

const hasTitleValue = computed(() =>
  Boolean(props.node.config?.title?.trim?.())
);
const hasTriggerKeywordsValue = computed(() =>
  Boolean(props.node.config?.triggerKeywords?.trim?.())
);
const matchTypeOptions = computed(() => [
  { value: 'exact', label: context.uiCopy.exactMatch },
  { value: 'contains', label: context.uiCopy.containsMatch },
]);
</script>

<template>
  <div
    class="bg-[linear-gradient(135deg,rgba(37,211,102,0.15),rgba(18,140,126,0.08))] px-3 py-2.5"
  >
    <div class="flex items-center justify-end gap-2">
      <NodeBadge
        :label="t('MOBILE_CONVERSATION_FLOWS.EDITOR.START_BADGE')"
        variant="whiteSoft"
      />
      <NodeBadge
        :label="
          node.config?.matchType === 'contains'
            ? context.uiCopy.containsMatch
            : context.uiCopy.exactMatch
        "
        variant="whiteOutline"
      />
    </div>
    <NodeInlineField
      v-if="isSelected"
      :model-value="node.config?.title || ''"
      :placeholder="context.uiCopy.nodeTitlePlaceholder"
      variant="plainStrong"
      @update:model-value="context.updateNode({ title: $event })"
    />
    <div
      v-else
      class="mt-2 text-[13px] font-semibold"
      :class="hasTitleValue ? 'text-n-slate-12' : 'text-n-slate-9'"
    >
      {{ node.config?.title || context.uiCopy.nodeTitlePlaceholder }}
    </div>
    <div
      v-if="hasTriggerKeywordsValue"
      class="mt-1.5 rounded-xl bg-white/75 px-2 py-1 text-[11px] text-n-slate-11"
    >
      {{ node.config.triggerKeywords }}
    </div>
  </div>

  <div
    v-if="isSelected"
    class="border-t border-black/5 bg-white px-3 py-2.5"
    @click.stop
  >
    <NodeSegmentedControl
      :model-value="node.config?.matchType || 'exact'"
      :options="matchTypeOptions"
      @update:model-value="context.updateNode({ matchType: $event })"
    />

    <div class="mt-2.5">
      <NodePanelInput
        :model-value="node.config?.triggerKeywords || ''"
        :placeholder="context.uiCopy.keywordPlaceholder"
        @update:model-value="context.updateNode({ triggerKeywords: $event })"
      />
    </div>

    <p class="mt-1.5 text-[10px] text-n-slate-10">
      {{ context.uiCopy.keywordHint }}
    </p>
  </div>
</template>
